import type { Content } from '@tiptap/react';
import * as React from 'react';

import { GUID, OpportunityView } from '@ndvr/customer-api';

import { MinimalTiptapEditor } from '@/components/minimal-tiptap';
import { MentionSuggestionItem } from '@/components/minimal-tiptap/extensions';
import { Button } from '@/components/ui/button';
import { convertPlainTextToHtml, hasVisibleTextContent } from 'utils';
import { extractMentionsFromHtml } from 'views/tasks/task-utils';
import { Toast } from 'widgets';

import { useCreateOpportunityComment } from '../opportunity-queries';

const getEditorStringValue = (value: Content) => (typeof value === 'string' ? value : '');

export interface OpportunityCommentSectionProps {
    opportunityGuid: GUID<OpportunityView>;
    draft: string;
    onDraftChange: (value: string) => void;
    mentionSuggestions: MentionSuggestionItem[];
}

export const OpportunityCommentSection = ({
    opportunityGuid,
    draft,
    onDraftChange,
    mentionSuggestions,
}: OpportunityCommentSectionProps) => {
    const createOpportunityComment = useCreateOpportunityComment();
    const [editorKey, setEditorKey] = React.useState(0);

    const handleSubmit = async () => {
        if (!hasVisibleTextContent(draft)) return;
        try {
            await createOpportunityComment.mutateAsync({
                opportunityGuid,
                request: {
                    content: draft,
                    mentions: extractMentionsFromHtml(draft),
                },
            });
            onDraftChange('');
            setEditorKey((k) => k + 1);
        } catch (error) {
            Toast.forError(error as Error, 'Failed to post comment');
        }
    };

    return (
        <div className="space-y-3">
            <div className="space-y-1">
                <h3 className="text-sm font-medium">Add Comment</h3>
                <p className="text-muted-foreground text-xs">
                    Comments and opportunity activity appear together in the timeline below.
                </p>
            </div>
            <MinimalTiptapEditor
                key={editorKey}
                mentions={mentionSuggestions}
                value={convertPlainTextToHtml(draft)}
                onChange={(nextValue) => onDraftChange(getEditorStringValue(nextValue))}
                className="w-full"
                editorContentClassName="p-5"
                output="html"
                placeholder="Write a comment..."
                editable={!createOpportunityComment.isPending}
                editorClassName="focus:outline-hidden"
            />
            <div className="flex justify-end">
                <Button
                    size="sm"
                    onClick={handleSubmit}
                    disabled={!hasVisibleTextContent(draft) || createOpportunityComment.isPending}
                >
                    {createOpportunityComment.isPending ? 'Posting…' : 'Post Comment'}
                </Button>
            </div>
        </div>
    );
};
